import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, http, LOCAL_API_URL } from "@/lib/constants";

// ---------- chain tip polling hook (blocks/latest + previous 4) ----------
export const useChainTip = (intervalMs = 20000) => {
  const [tip, setTip] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState(null);
  const retryRef = useRef(null);
  const hasTip = useRef(false);

  const fetchTip = useCallback(async () => {
    try {
      const tipRes = await http.get(`${API}/blocks/latest`, { timeout: 15000 });
      if (!tipRes.data || typeof tipRes.data !== "object" || tipRes.data.error) {
        throw new Error("Unexpected chain-tip payload");
      }
      setTip(tipRes.data);
      hasTip.current = true;
      if (tipRes.data?.hash) {
        const prevRes = await http.get(
          `${API}/blocks/${tipRes.data.hash}/previous`,
          { params: { count: 4 }, timeout: 15000 },
        );
        // /previous is desc by default — sort descending by height for display
        const list = Array.isArray(prevRes.data)
          ? [...prevRes.data].sort((a, b) => (b.height ?? 0) - (a.height ?? 0))
          : [];
        setRecent([tipRes.data, ...list]);
      } else {
        setRecent([tipRes.data]);
      }
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || { message: e.message });
      // Fast self-heal while we still have nothing to show.
      if (!hasTip.current) {
        clearTimeout(retryRef.current);
        retryRef.current = setTimeout(fetchTip, 6000);
      }
    }
  }, []);

  useEffect(() => {
    fetchTip();
    const id = setInterval(fetchTip, intervalMs);
    return () => { clearInterval(id); clearTimeout(retryRef.current); };
  }, [fetchTip, intervalMs]);

  return { tip, recent, error, refresh: fetchTip };
};

// ---------- external Cardano data API status/maintenance polling hook ----------
// `maintenance` reflects both "unreachable" AND "reachable but its own DB is
// down" (the external /api/health returns 200 even mid-outage) — drives both
// the homepage's small status badge and the explorer/docs maintenance banner.
export const useExternalStatus = (intervalMs = 60000) => {
  // Optimistic default (green/online) until the first check resolves, rather
  // than a "checking…" flash — the backend already masks brief real outages
  // behind a 60s grace period, so a stale-green flash here is low-risk.
  const [status, setStatus] = useState({ online: true, maintenance: false, apiStatus: "online" });

  const check = useCallback(async () => {
    try {
      const res = await axios.get(`${LOCAL_API_URL}/api/external-status`, { timeout: 12000 });
      setStatus({
        online: res.data?.online === true,
        maintenance: res.data?.maintenance === true,
        apiStatus: res.data?.status || null,
      });
    } catch {
      // Our own monitoring endpoint was unreachable/timed out — we genuinely
      // can't confirm the provider's state, so report "unreachable" (rendered
      // as a neutral "status unknown") WITHOUT raising the red maintenance banner.
      setStatus({ online: false, maintenance: false, apiStatus: "unreachable" });
    }
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, intervalMs);
    return () => clearInterval(id);
  }, [check, intervalMs]);

  return status;
};

// ---------- network stats polling hook (pools/tx-count/rewards/active-stake) ----------
export const useNetworkStats = (intervalMs = 60000) => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const retryRef = useRef(null);
  const hasData = useRef(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await http.get(`${API}/network/stats`, { timeout: 15000 });
      if (!res.data || typeof res.data !== "object" || Array.isArray(res.data) || res.data.error) {
        throw new Error("Unexpected network-stats payload");
      }
      setStats(res.data);
      hasData.current = true;
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || { message: e.message });
      // Fast self-heal while we still have nothing to show (don't wait 60s).
      if (!hasData.current) {
        clearTimeout(retryRef.current);
        retryRef.current = setTimeout(fetchStats, 6000);
      }
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const id = setInterval(fetchStats, intervalMs);
    return () => { clearInterval(id); clearTimeout(retryRef.current); };
  }, [fetchStats, intervalMs]);

  return { stats, error };
};
